import React from "react";
import { Table, Button, Select, Tag, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { Candidate, Status, getStatusColor } from "../types";

const { Option } = Select;

interface CandidateTableProps {
  candidates: Candidate[];
  onStatusChange: (id: string, status: Status) => void;
  onDelete: (id: string) => void;
  onView: (candidate: Candidate) => void;
}

export const CandidateTable: React.FC<CandidateTableProps> = ({
  candidates,
  onStatusChange,
  onDelete,
  onView,
}) => {
  const columns: ColumnsType<Candidate> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Button onClick={() => onView(record)} style={{ fontWeight: 600 }}>
          {text}
        </Button>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Date Applied",
      dataIndex: "appliedDate",
      key: "appliedDate",
      sorter: (a, b) =>
        new Date(a.appliedDate).getTime() - new Date(b.appliedDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Select
          defaultValue={status}
          style={{ width: 120 }}
          onChange={(val) => onStatusChange(record.id, val)}
        >
          {[
            "Applied",
            "Screening",
            "Interview",
            "Offer",
            "Hired",
            "Rejected",
          ].map((s) => (
            <Option key={s} value={s}>
              <Tag color={getStatusColor(s as Status)}>{s}</Tag>
            </Option>
          ))}
        </Select>
      ),
      filters: [
        { text: "Active (Screening/Interview)", value: "active" },
        { text: "Hired", value: "Hired" },
        { text: "Rejected", value: "Rejected" },
      ],
      onFilter: (value, record) => {
        if (value === "active")
          return ["Screening", "Interview", "Offer", "Applied"].includes(
            record.status
          );
        return record.status === value;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Popconfirm
          title="Delete candidate?"
          onConfirm={() => onDelete(record.id)}
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={candidates}
      rowKey="id"
      pagination={{ pageSize: 10 }}
      locale={{ emptyText: "No Candidates Found. Add one to get started!" }}
    />
  );
};
